import { TrialHomeCard, TrialHomeContent } from '@chiroup/core/types/TrialHome.type';
import { ChevronRightIcon, LockClosedIcon, SparklesIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { TRIAL_CARD_ICON_COMPONENTS, TRIAL_CARD_ICON_FALLBACK } from './cardIcons';

/**
 * Read-only live preview of the Trial Home, mirroring what trial users see in
 * the chiroup app. Click handlers are wired to {@link onSelectCard} instead
 * of firing tours so admins can jump straight to a card's editor from the
 * preview. Visual styling intentionally tracks the chiroup TrialHome.tsx so
 * "what marketing sees here" matches "what users see live".
 */

type Props = {
  data: TrialHomeContent;
  selectedCardId?: string | null;
  onSelectCard?: (id: string) => void;
};

const TrialHomePreview: React.FC<Props> = ({ data, selectedCardId, onSelectCard }) => {
  // Section + order come from the cards array itself — same logic as the
  // live chiroup app, so the admin preview never drifts.
  const essentialsCards = data.cards.filter(c => c.enabled && c.section === 'essentials');
  const ehrCards = data.cards.filter(c => c.enabled && c.section === 'ehr');

  return (
    <div className="pb-8 bg-gray-50 dark:bg-dark-gray-900 min-h-full text-left">
      {/* Greeting header */}
      <div className="pt-5 pb-5 px-5 sm:px-6 bg-white dark:bg-dark-gray-800 border-b border-gray-200 dark:border-dark-gray-700">
        <h1 className="text-xl font-bold text-gray-900 dark:text-dark-gray-100">Hello, Doctor</h1>
        <div className="mt-1 flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-dark-gray-700 dark:text-dark-gray-300">
            Test
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
            <SparklesIcon className="h-3 w-3" />
            Trial
          </span>
        </div>
      </div>

      <div className="px-4 sm:px-5 mt-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-dark-gray-100 mb-4">
          {data.heading}
        </h2>

        {essentialsCards.length > 0 && (
          <Section label={data.essentialsLabel} accent="primary">
            {essentialsCards.map(card => (
              <PreviewCard
                key={card.id}
                card={card}
                variant="essentials"
                selected={selectedCardId === card.id}
                onClick={() => onSelectCard?.(card.id)}
              />
            ))}
          </Section>
        )}

        {ehrCards.length > 0 && (
          <Section label={data.ehrLabel} className="mt-8" accent="muted">
            {ehrCards.map(card => (
              <PreviewCard
                key={card.id}
                card={card}
                variant="ehr"
                selected={selectedCardId === card.id}
                onClick={() => onSelectCard?.(card.id)}
              />
            ))}
          </Section>
        )}

        {/* CTA */}
        <div className="mt-10 relative overflow-hidden rounded-2xl border border-primary-100 dark:border-primary-900/40 bg-gradient-to-br from-primary-50 via-white to-primary-50/40 dark:from-primary-900/20 dark:via-dark-gray-800 dark:to-primary-900/10 p-6 text-center">
          <div
            aria-hidden
            className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-primary-200/50 dark:bg-primary-700/20 blur-3xl"
          />
          <div className="relative">
            <p className="text-lg font-bold tracking-tight text-gray-900 dark:text-dark-gray-100">
              {data.ctaTitle}
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-semibold text-xs shadow">
              {data.ctaButton}
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </div>
            {data.ctaSubtitle && (
              <p className="mt-3 text-[11px] text-gray-500 dark:text-dark-gray-400 max-w-md mx-auto">
                {data.ctaSubtitle}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{
  label: string;
  className?: string;
  accent?: 'primary' | 'muted';
  children: React.ReactNode;
}> = ({ label, className, accent = 'primary', children }) => (
  <div className={className}>
    <div className="flex items-center gap-2 mb-3">
      <span
        className={[
          'h-1 w-5 rounded-full',
          accent === 'primary' ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-gray-600',
        ].join(' ')}
      />
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400">
        {label}
      </p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
  </div>
);

const PreviewCard: React.FC<{
  card: TrialHomeCard;
  variant: 'essentials' | 'ehr';
  selected: boolean;
  onClick: () => void;
}> = ({ card, variant, selected, onClick }) => {
  const Icon = TRIAL_CARD_ICON_COMPONENTS[card.icon] ?? TRIAL_CARD_ICON_FALLBACK;
  const highlighted = !!card.badge;
  const isEhr = variant === 'ehr';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group relative text-left rounded-xl p-3 transition-all overflow-hidden border',
        selected
          ? 'border-primary-500 ring-2 ring-primary-400/40 shadow-md'
          : highlighted
            ? 'bg-white dark:bg-dark-gray-800 border-primary-300 ring-2 ring-primary-200/60 dark:border-primary-700 dark:ring-primary-900/40 shadow-sm'
            : isEhr
              ? 'bg-white/70 dark:bg-dark-gray-800/70 border-gray-200/80 dark:border-dark-gray-700'
              : 'bg-white dark:bg-dark-gray-800 border-gray-200 dark:border-dark-gray-700',
      ].join(' ')}
    >
      {highlighted && card.badge && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-primary-500 text-white shadow-sm">
          <SparklesIcon className="h-2.5 w-2.5" />
          {card.badge}
        </span>
      )}
      {!highlighted && isEhr && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider bg-gray-100 text-gray-500 dark:bg-dark-gray-700 dark:text-dark-gray-400">
          <LockClosedIcon className="h-2 w-2" />
          Preview
        </span>
      )}

      <div className="relative flex flex-col h-full">
        <div
          className={[
            'inline-flex items-center justify-center w-9 h-9 rounded-lg mb-2',
            highlighted
              ? 'bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow shadow-primary-500/30'
              : isEhr
                ? 'bg-gray-100 text-gray-500 dark:bg-dark-gray-700 dark:text-dark-gray-400'
                : 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300',
          ].join(' ')}
        >
          <Icon className="w-5 h-5" />
        </div>
        <h3
          className={[
            'text-sm font-semibold tracking-tight',
            isEhr
              ? 'text-gray-700 dark:text-dark-gray-200'
              : 'text-gray-900 dark:text-dark-gray-100',
          ].join(' ')}
        >
          {card.title}
        </h3>
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-dark-gray-400 leading-snug">
          {card.description}
        </p>
      </div>
    </button>
  );
};

export default TrialHomePreview;
